import { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router';

import { keyframes, legacyTheme, styled } from '@prose-ui/legacy';
import { withStyles } from 'legacyStyles';

import { SocialMediaGridItem } from 'Components/GridItem';
import { Button } from 'Components/LegacyButton';
import { MetaData } from 'Components/MetaData';
import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';
import HeroWithBackground from 'Blocks/HeroWithBackground';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import content from 'assets/content/contact';
import { ReactComponent as Stroke } from 'assets/images/stroke-contact.svg';

const dashAnimation = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const styles = {
  path: {
    marginTop: '-50px',
    marginBottom: '-50px',

    strokeDasharray: 133,
    strokeDashoffset: -133,

    '&.in-view': {
      animation: `${dashAnimation} 2s linear forwards`,
    },
  },
  hero: {
    display: 'grid',
    gridTemplateColumns: '100%',
    minHeight: 375,

    backgroundPosition: 'right top',

    [legacyTheme.breakpoints.match('xs')]: {
      padding: 0,
    },

    [legacyTheme.breakpoints.up('sm')]: {
      gridTemplateColumns: '50% 50%',
    },
  },
  grid: {
    display: 'grid',
    gridTemplate: `"gridItem1 gridItem2"
                   "gridItem3 gridItem4"
                   "gridItem5 gridItem5"
                   / 1fr 1fr`,
    width: '100%',
    columnGap: `${legacyTheme.spacing.s32}`,
    rowGap: `${legacyTheme.spacing.s72}`,
    justifyItems: 'center',

    [legacyTheme.breakpoints.match('md')]: {
      gridTemplate: 'auto / 1fr',
    },
  },
  block2: {
    [legacyTheme.breakpoints.match('sm')]: {
      padding: `0 ${legacyTheme.spacing.s24}`,
    },

    '& a': {
      marginBottom: legacyTheme.spacing.s72,
    },
  },
  center: {
    display: 'grid',
    placeItems: 'center',
    gap: `${legacyTheme.spacing.s16}`,
  },
  maxWidth: {
    maxWidth: '400px',

    [legacyTheme.breakpoints.match('xs')]: {
      padding: `0 ${legacyTheme.spacing.s24}`,
    },
  },
  blockContent: {
    width: '100%',
    maxWidth: '800px',
    marginTop: `${legacyTheme.spacing.s64}`,
    marginBottom: '114px',

    [legacyTheme.breakpoints.match('sm')]: {
      margin: `${legacyTheme.spacing.s72} auto ${legacyTheme.spacing.s96} auto`,
    },
  },
  block2FirstContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '18px',

    fontSize: '28px',

    [legacyTheme.breakpoints.up('md')]: {
      height: '90px',
      marginBottom: `${legacyTheme.spacing.s24}`,
    },
  },
  block2SndContent: {
    marginBottom: `${legacyTheme.spacing.s32}`,
    fontSize: '14px',
  },
  block3rdContent: {
    marginBottom: '50px',
  },
  narrow: {
    minWidth: legacyTheme.width.narrow,
  },
  blockContent3: {
    margin: `${legacyTheme.spacing.s96} auto`,
  },
};

const StyledGridItem = styled(SocialMediaGridItem)({
  display: 'flex',
  gridArea: 'auto',
  justifyContent: 'space-between',
  maxWidth: 570,
  marginBottom: 'initial',

  // We don't need to define it for gridItem[1-4] because gridArea defaut value is auto
  '&:nth-of-type(5)': {
    gridArea: 'gridItem5',

    [legacyTheme.breakpoints.match('md')]: {
      gridArea: 'auto',
    },
  },
});

const Contact = ({ classes }) => {
  const [ref, inView] = useInView({
    /* Optional options */
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <>
      <MetaData
        description="Have a question about your order? Need help during your consultation? Our team of in-house experts is here for you. 100% satisfaction guaranteed on all orders."
        title="Get Help from Hair Care Specialists | Contact Prose Hair team"
      />

      <BaseScene footer header>
        <HeroWithBackground
          classes={{ root: classes.hero }}
          columnOffset={0}
          content={{
            imageDesktop: content.hero.imageDesktop,
            imageMobile: content.hero.imageMobile,
            imageTablet: content.hero.imageTablet,
          }}
        >
          <div className={classes.center}>
            <Typography className={classes.maxWidth} color="white" variant="h1">
              {content.hero.title}
            </Typography>
            <Button
              classes={{ root: classes.narrow }}
              Component={Link}
              to="/faq"
              variant="whiteFull"
            >
              {content.block2.link}
            </Button>
          </div>
        </HeroWithBackground>

        <BlockLayout
          backgroundColor="beige"
          classes={{ root: classes.block2, content: classes.blockContent }}
          contentAlign="center"
        >
          <Typography className={classes.block2FirstContent} variant="h1">
            {content.block2.title}
          </Typography>
          <Typography className={classes.block2SndContent} variant="p1">
            {content.block2.subtitle}
          </Typography>
          <Typography className={classes.block3rdContent} paragraph variant="mono2">
            {content.block2.availableFrom}
          </Typography>
        </BlockLayout>

        <Stroke ref={ref} className={classNames(classes.path, { 'in-view': inView })} />

        <BlockLayout
          classes={{ content: classes.blockContent3 }}
          contentAlign="center"
          contentWidth="veryWide"
        >
          <div className={classes.grid}>
            {content.block3.map((entries, idx) => (
              <StyledGridItem key={String(idx)}>
                {entries.map((entry, index) => (
                  <Fragment key={String(index)}>{entry}</Fragment>
                ))}
              </StyledGridItem>
            ))}
          </div>
        </BlockLayout>
      </BaseScene>
    </>
  );
};

Contact.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default withStyles(Contact, styles);
